import { ChangeEvent } from 'react';
import { Label, Radio, Select, Tag, Text, Toggle } from '@nimbus-ds/components';
import { QuestionCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import {
  GetDocumentPageSizeSettingResponseDto,
  GetDocumentSettingsResponseDto,
} from '@tiendanube/common';
import { Tooltip } from '@tiendanube/components';
import { InputNumberNimbus, Stack, useResponsive } from 'commons/components';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';
import DocumentPageSettingsFormSkeleton from './Skeleton';
import { CUSTOM_PAGE_SIZE_CODE, DocumentType } from '../../constants';

interface DocumentPageSettingsFormProps {
  settings: GetDocumentSettingsResponseDto;
  type: DocumentType;
  hasIncorrectSizes: {
    width: boolean;
    height: boolean;
  };
  onChangePageSize: (
    name: string,
    value: GetDocumentPageSizeSettingResponseDto,
  ) => void;
  onChangeSetting: (name: string, value: boolean) => void;
  onChangeNumberSetting: (name: string, value: number) => void;
}

function DocumentPageSettingsForm({
  settings,
  type,
  hasIncorrectSizes,
  onChangePageSize,
  onChangeSetting,
  onChangeNumberSetting,
}: Readonly<DocumentPageSettingsFormProps>) {
  const t = useTranslationFulfillmentOrders('printManager');
  const { isMobile } = useResponsive();
  const isShippingDocument = type === DocumentType.SHIPPING_DOCUMENT;

  const {
    pageSize: selectedPageSize,
    availablePageSizes,
    downloadDocument,
    showHorizontalDocumentsPerPage,
  } = settings;

  const columnsOptions = [
    {
      value: 1,
      label: t('pageSize.oneColumn'),
      checked: showHorizontalDocumentsPerPage === 1,
    },
    {
      value: 2,
      label: t('pageSize.twoColumns'),
      checked: showHorizontalDocumentsPerPage === 2,
    },
  ];

  const handleOnChangePageSize = (value: string) => {
    if (value === CUSTOM_PAGE_SIZE_CODE) {
      const customPageSize = {
        code: CUSTOM_PAGE_SIZE_CODE,
        width: selectedPageSize.width,
        height: selectedPageSize.height,
      };
      onChangePageSize('pageSize', customPageSize);
    } else {
      const pageSize = availablePageSizes.find(({ code }) => code === value);
      if (pageSize) {
        onChangePageSize('pageSize', pageSize);
      }
    }
  };

  const onChangeCustomSize = ({
    target: { name, value },
  }: ChangeEvent<HTMLInputElement>) => {
    const customPageSize = {
      code: CUSTOM_PAGE_SIZE_CODE,
      width: name === 'width' ? value : selectedPageSize.width,
      height:
        name === 'height' ? (value ? value : 'auto') : selectedPageSize.height,
    };
    onChangePageSize('pageSize', customPageSize);
  };

  return (
    <>
      <hr />
      <Stack column align="stretch" spacing="loose">
        {isShippingDocument && (
          <Stack column align="stretch" spacing="tight">
            <Stack align="center" justify="space-between">
              <Label>{t('pageSize.columnsPerPage')}</Label>
              <Tag appearance="primary">{t('informationCard.newTag')}</Tag>
            </Stack>
            <Stack spacing="tight">
              {columnsOptions.map((option) => (
                <Radio
                  key={option.value}
                  id={`${option.value}-column`}
                  label={option.label}
                  name="showHorizontalDocumentsPerPage"
                  value={String(option.value)}
                  onChange={() =>
                    onChangeNumberSetting(
                      'showHorizontalDocumentsPerPage',
                      option.value,
                    )
                  }
                  as="button"
                  checked={option.checked}
                />
              ))}
            </Stack>
          </Stack>
        )}
        {!isMobile && (
          <Stack column align="stretch" spacing="tight">
            <Label>{t('pageSize.title')}</Label>
            <Stack align="center" alignSelf="flex-start" spacing="tight">
              <Toggle
                name="downloadToggle"
                checked={downloadDocument}
                label={t('pageSize.downloadDocument')}
                onChange={() =>
                  onChangeSetting('downloadDocument', !downloadDocument)
                }
              />
              <Tooltip
                position="right"
                labelIcon={QuestionCircleIcon}
                labelText={t('pageSize.downloadDocumentInfo')}
              >
                <Text fontSize="caption" color="neutral-background">
                  {t('pageSize.downloadDocumentInfo')}
                </Text>
              </Tooltip>
            </Stack>
          </Stack>
        )}
        {(downloadDocument || isMobile) && (
          <Stack column align="stretch" spacing="tight">
            <FormField.Select
              label={t('pageSize.label')}
              helpText={t('pageSize.helpText.' + type)}
              showHelpText
              name="pageSize"
              id="pageSize"
              onChange={(event) => handleOnChangePageSize(event.target.value)}
            >
              {availablePageSizes.map(({ code, height, width }) => (
                <Select.Option
                  key={code}
                  value={code}
                  label={`${code} - (${width}cm x${height}cm)`}
                  selected={selectedPageSize.code === code}
                />
              ))}
              <Select.Option
                key="custom"
                value="custom"
                selected={selectedPageSize.code === 'custom'}
                label={t('pageSize.custom')}
              />
            </FormField.Select>
            {selectedPageSize.code === 'custom' && (
              <Stack wrap column align="stretch" spacing="tight">
                <Stack.Item fill>
                  <FormField
                    label={t('pageSize.customInputs.width')}
                    showHelpText={hasIncorrectSizes.width}
                    helpText={t('pageSize.customInputs.widthHelpText')}
                  >
                    <InputNumberNimbus
                      id="width"
                      type="float"
                      defaultDecimals={2}
                      append={t('pageSize.customInputs.append')}
                      appendPosition="end"
                      onChange={onChangeCustomSize}
                      value={selectedPageSize.width}
                      name="width"
                      appearance={
                        hasIncorrectSizes.width ? 'danger' : 'neutral'
                      }
                    />
                  </FormField>
                </Stack.Item>
                <Stack.Item fill>
                  <FormField
                    label={t('pageSize.customInputs.height')}
                    showHelpText={hasIncorrectSizes.height}
                    helpText={t('pageSize.customInputs.heightHelpText')}
                  >
                    <InputNumberNimbus
                      id="height"
                      type="float"
                      defaultDecimals={2}
                      append={t('pageSize.customInputs.append')}
                      appendPosition="end"
                      placeholder={t('pageSize.customInputs.heightPlaceholder')}
                      onChange={onChangeCustomSize}
                      value={
                        selectedPageSize.height === 'auto'
                          ? ''
                          : selectedPageSize.height
                      }
                      name="height"
                      appearance={
                        hasIncorrectSizes.height ? 'danger' : 'neutral'
                      }
                    />
                  </FormField>
                </Stack.Item>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}

DocumentPageSettingsForm.Skeleton = DocumentPageSettingsFormSkeleton;
export default DocumentPageSettingsForm;
